import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Rnd } from 'react-rnd';
import Close from '@material-ui/icons/Close';
import Fullscreen from '@material-ui/icons/Fullscreen';
import FullscreenExit from '@material-ui/icons/FullscreenExit';
import IconButton from '@material-ui/core/IconButton';
import styled from 'src/ui/library/styled';
import ContentDebuggerTree from './ContentDebuggerTree';

const styles = theme => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: 'solid 1px #ddd',
    background: '#000',
    zIndex: 1000,
  },
  cancel: {
    cursor: 'pointer',
  },
  iconBtn: {
    width: '10%',
  },
  nav: {
    backgroundColor: '#343A46',
    display: 'flex',
    height: '40px',
    alignItems: 'center',
  },
  fixedNav: {
    width: '30vw',
  },
  navTitle: {
    width: '60%',
    color: '#EFEFF1',
    textAlign: 'center',
    fontSize: '16px',
  },
  navSelect: {
    width: 'calc(30% - 16px)',
    paddingRight: '16px',
    height: '20px',
  },
  navSelectOption: {
    margin: '0px',
    minHeight: 'auto',
    height: '20px',
  },
  wrapper: {
    overflow: 'scroll',
    width: '100%',
    height: '100%',
  },
  fixedWrapper: {
    overflow: 'scroll',
    width: '30vw',
    height: '100vh',
    position: 'fixed',
    right: 0,
    top: 0,
    border: 'solid 1px #ddd',
    background: '#000',
    zIndex: 1000,
  },
  list: {
    listStyleType: 'none',
    padding: '0px',
    margin: '8px 8px 16px 16px',
  },
  listItem: {
    color: 'rgb(111, 179, 210)',
    marginBottom: '8px',
    fontFamily: 'monaco, Consolas, "Lucida Console", monospace',
  },
  formValues: {
    width: '100%',
    maxWidth: '400px',
    wordBreak: 'break-word',
    color: '#FFF',
  },
  elProps: {
    listStyleType: 'none',
    margin: '0px',
    padding: '0px',
    paddingLeft: '8px',
    color: 'rgb(161, 198, 89)',
    '& span': {
      color: 'rgb(111, 179, 210)',
    },
  },
  false: {
    color: 'rgb(252, 109, 36)',
  },
  icon: {
    color: '#FFF',
  },
  content: {
    cursor: 'auto',
    width: '100%',
    height: '100%',
  },
  fixedContent: {
    cursor: 'auto',
    width: '50vw',
    height: '100%',
  },
});

class ContentDebugger extends React.Component {
  static propTypes = {
    /** Classname to be applied to Content Parser */
    className: PropTypes.string,
    /** Styles to be applied to component */
    classes: PropTypes.object,
    /** Element function access to parser state */
    elementActions: PropTypes.func.isRequired,
    /** Callback fired when value is changed */
    onChange: PropTypes.func,
  };

  static defaultProps = {};

  state = {
    showDebugger: true,
    viewName: 'elements',
    fixDebugger: false,
  };

  componentDidMount() {
    this.statsTimer = setInterval(() => {
      this.setState({ updateStats: true });
    }, 500);
  }

  componentWillUnmount() {
    clearInterval(this.statsTimer);
  }

  switchView = viewName => {
    this.setState({ viewName });
  };

  hideDebugger = () => {
    clearInterval(this.statsTimer);
    this.setState({ showDebugger: false });
  };

  onMouseOver = domNode => {
    if (domNode) {
      domNode.classList.add('parser-debug');
    }
  };

  onMouseOut = domNode => {
    if (domNode) {
      domNode.classList.remove('parser-debug');
    }
  };

  getElementProperties = element => {
    const {
      onChange,
      complexShowLogic,
      logicId,
      controlTypeName,
      componentRef,
      showLogic,
      isRootLevel,
      component,
      updateComponent,
      props,
      ...otherProps // eslint-disable-line
    } = element;
    const { classes } = this.props;

    const displayProps = {
      isRootLevel,
      controlType: controlTypeName,
      showLogicId: logicId,
      showLogic: showLogic || complexShowLogic ? 'provided' : 'not provided',
      ...props,
    };

    const displayList = Object.entries(displayProps).map(([key, val]) => {
      let propVal = val !== null && val !== undefined ? val.toString() : 'null';

      const liClass = classNames({
        [classes.false]: propVal === 'false',
      });

      return (
        <li key={key} className={liClass}>
          <span>{key}:</span> {propVal}
        </li>
      );
    });

    return <ul className={classes.elProps}>{displayList}</ul>;
  };

  getElements = data => {
    const { classes } = this.props;

    const renderMap = data.map(el => {
      const compRef = el.componentRef || null;
      return (
        <li
          className={classes.listItem}
          onMouseOver={this.onMouseOver.bind(null, compRef)}
          onMouseOut={this.onMouseOut.bind(null, compRef)}
          key={el.id}>
          <ContentDebuggerTree label={el.id}>{this.getElementProperties(el)}</ContentDebuggerTree>
        </li>
      );
    });

    return renderMap;
  };

  getIndexes = data => {
    const { classes } = this.props;

    const renderMap = Object.entries(data).map(([key, val]) => {
      return (
        <li className={classes.listItem} key={key}>
          <ContentDebuggerTree label={key}>{val.toString()}</ContentDebuggerTree>
        </li>
      );
    });

    return renderMap;
  };

  getFormValues = data => {
    const { classes } = this.props;

    return <li className={classes.formValues}>{JSON.stringify(data)}</li>;
  };

  getView = elementActions => {
    const { getParserState } = elementActions();
    const parserState = getParserState();
    const { viewName } = this.state;

    if (viewName === 'elements') {
      return this.getElements(parserState.elements);
    } else if (viewName === 'indexes') {
      return this.getIndexes(parserState.logicIndex);
    } else if (viewName === 'formValues') {
      return this.getFormValues(parserState.formValues);
    }
  };

  toggleFixDebugger = () => this.setState(prevState => ({ fixDebugger: !prevState.fixDebugger }));

  renderDebugger = () => {
    const { classes, elementActions } = this.props;
    const { fixDebugger } = this.state;
    return (
      <div className={fixDebugger ? classes.fixedWrapper : classes.wrapper}>
        <nav className={fixDebugger ? `${classes.nav} ${classes.fixedNav}` : classes.nav}>
          <IconButton className={classes.iconBtn} onClick={this.toggleFixDebugger}>
            {fixDebugger ? <FullscreenExit className={classes.icon} /> : <Fullscreen className={classes.icon} />}
          </IconButton>
          <h2 className={classes.navTitle}>Parser Inspector</h2>
          <select className={classes.navSelect} onChange={event => this.switchView(event.target.value)}>
            <option className={classes.navSelectOption} value="elements">
              Elements
            </option>
            <option className={classes.navSelectOption} value="indexes">
              Indexes
            </option>
            <option className={classes.navSelectOption} value="formValues">
              Form Values
            </option>
          </select>
          <IconButton className={classes.iconBtn} onClick={this.hideDebugger}>
            <Close className={classes.icon} />
          </IconButton>
        </nav>
        <div className={classNames(fixDebugger ? classes.fixedContent : classes.content, 'cancel')}>
          <ul className={classes.list}>{this.getView(elementActions)}</ul>
        </div>
      </div>
    );
  };

  render() {
    const { showDebugger, fixDebugger } = this.state;
    if (!showDebugger) return null;

    const { classes } = this.props;

    return fixDebugger ? (
      this.renderDebugger()
    ) : (
      <Rnd
        className={classes.root}
        cancel={'.cancel'}
        default={{
          x: 700,
          y: 0,
          height: 400,
          width: 700,
        }}>
        {this.renderDebugger()}
      </Rnd>
    );
  }
}

export default styled(ContentDebugger)(styles);



// WEBPACK FOOTER //
// ./src/ui/library/ContentParser/ContentDebugger.js